export type Chain = 'SOLANA' | 'ETHEREUM' | 'BSC' | 'BASE' | 'OMNI';
export const SOLANA_CHAIN_ID = '100000';
export const ETHEREUM_CHAIN_ID = '1';
export const BASE_CHAIN_ID = '8453';
export const BSC_CHAIN_ID = '56';
export const OMNI_CHAIN_ID = '110000';

export const AVAILABLE_CHAINS: Chain[] = ['SOLANA', 'ETHEREUM', 'BSC'];
export const CHAIN_IDS: Record<Chain, number> = {
  SOLANA: 100000,
  ETHEREUM: 1,
  BSC: 56,
  BASE: 8453,
  OMNI: 110000,
} as const;
export const CHAIN_ID_TO_LOWER_NAME: { [key: string]: Lowercase<Chain> } = {
  [CHAIN_IDS.SOLANA]: 'solana',
  [CHAIN_IDS.ETHEREUM]: 'ethereum',
  [CHAIN_IDS.BSC]: 'bsc',
  [CHAIN_IDS.BASE]: 'base',
  [CHAIN_IDS.OMNI]: 'omni',
};
export const CHAIN_LOWER_NAME_TO_ID: { [key in Lowercase<Chain>]: number } = {
  solana: CHAIN_IDS.SOLANA,
  ethereum: CHAIN_IDS.ETHEREUM,
  bsc: CHAIN_IDS.BSC,
  base: CHAIN_IDS.BASE,
  omni: CHAIN_IDS.OMNI,
};
export const CHAIN_TOKENS: { [key in Lowercase<Chain>]: string } = {
  solana: 'SOL',
  ethereum: 'ETH',
  bsc: 'BNB',
  base: 'BASE_ETH',
  omni: 'OMNI',
};

export const CHAIN_ID_TO_CHAIN_TOKEN: Record<
  (typeof CHAIN_IDS)[keyof typeof CHAIN_IDS],
  string
> = {
  [CHAIN_IDS.SOLANA]: 'SOL',
  [CHAIN_IDS.ETHEREUM]: 'ETH',
  [CHAIN_IDS.BSC]: 'BNB',
  [CHAIN_IDS.BASE]: 'BASE_ETH',
};
export interface ChainInfo {
  id: string;
  name: string;
  shortName: string;
  chainName: string;
  symbol: string;
  icon: string;
  decimals: number;
  fundName: string;
  explorerIcon: string;
  gasTypeName: string;
  gasTypeNameCamel: string;
  gasName: string;
  gasSettingName: '' | 'Price';
  getTokenExplorerUrl: (address: string, type?: string) => string;
}

export const CHAIN_INFO: Record<string, ChainInfo> = {
  OMNI: {
    id: OMNI_CHAIN_ID,
    name: 'Omni Chain',
    shortName: 'Omni Chain',
    symbol: 'USD',
    fundName: 'usd',
    decimals: 18,
    icon: 'https://www.pumpx.ai/images/Omnichain.png',
    explorerIcon: 'https://www.pumpx.ai/images/Omnichain.png',
    getTokenExplorerUrl: (val: string, type = 'account') => '',
    gasTypeName: 'gas_type_sol',
    gasTypeNameCamel: 'gasTypeSol',
    gasName: 'USD',
    gasSettingName: '',
    chainName: 'omni',
  },
  SOL: {
    id: SOLANA_CHAIN_ID,
    name: 'SOL',
    shortName: 'SOL',
    symbol: 'SOL',
    fundName: 'solana',
    chainName: 'solana',
    decimals: 9,
    icon: 'https://s2.coinmarketcap.com/static/img/coins/64x64/5426.png',
    explorerIcon:
      'https://s2.coinmarketcap.com/static/img/coins/64x64/5426.png',
    getTokenExplorerUrl: (val: string, type = 'account') => {
      switch (type) {
        case 'account': {
          return `https://solscan.io/account/${val}`;
        }
        case 'txHash': {
          return `https://solscan.io/tx/${val}`;
        }
        default: {
          return '';
        }
      }
    },
    gasTypeName: 'gas_type_sol',
    gasTypeNameCamel: 'gasTypeSol',
    gasName: 'SOL',
    gasSettingName: '',
  },
  BSC: {
    id: BSC_CHAIN_ID,
    name: 'BSC',
    shortName: 'BSC',
    chainName: 'bsc',
    decimals: 18,
    fundName: 'bnb',
    symbol: 'BNB',
    icon: 'https://s2.coinmarketcap.com/static/img/coins/64x64/1839.png',
    explorerIcon:
      'https://s2.coinmarketcap.com/static/img/coins/64x64/1839.png',
    getTokenExplorerUrl: (val: string, type = 'account') => {
      switch (type) {
        case 'account': {
          return `https://bscscan.com/address/${val}`;
        }
        case 'txHash': {
          return `https://bscscan.com/tx/${val}`;
        }
        default: {
          return '';
        }
      }
    },
    gasTypeName: 'gas_type_bsc',
    gasTypeNameCamel: 'gasTypeBsc',
    gasName: 'Gwei',
    gasSettingName: 'Price',
  },
  BASE: {
    id: BASE_CHAIN_ID,
    name: 'BASE',
    shortName: 'BASE',
    chainName: 'base',
    fundName: 'base_eth',
    decimals: 18,
    symbol: 'BASE_ETH',
    icon: 'https://s2.coinmarketcap.com/static/img/coins/64x64/27716.png', // Base专有图标，可替换为实际链接
    explorerIcon:
      'https://s2.coinmarketcap.com/static/img/coins/64x64/27716.png', // Base专有图标，可替换为实际链接
    getTokenExplorerUrl: (val: string, type = 'account') => {
      switch (type) {
        case 'account': {
          return `https://basescan.org/address/${val}`;
        }
        case 'txHash': {
          return `https://basescan.org/tx/${val}`;
        }
        default: {
          return '';
        }
      }
    },
    gasTypeName: 'gas_type_base',
    gasTypeNameCamel: 'gasTypeBase',
    gasName: 'Gwei',
    gasSettingName: 'Price',
  },
  ETH: {
    id: ETHEREUM_CHAIN_ID,
    name: 'ETH',
    shortName: 'ETH',
    chainName: 'ethereum',
    fundName: 'eth',
    decimals: 18,
    symbol: 'ETH',
    icon: 'https://s2.coinmarketcap.com/static/img/coins/64x64/1027.png',
    explorerIcon:
      'https://s2.coinmarketcap.com/static/img/coins/64x64/1027.png',
    getTokenExplorerUrl: (val: string, type = 'account') => {
      switch (type) {
        case 'account': {
          return `https://etherscan.io/address/${val}`;
        }
        case 'txHash': {
          return `https://etherscan.io/tx/${val}`;
        }
        default: {
          return '';
        }
      }
    },
    gasTypeName: 'gas_type_eth',
    gasTypeNameCamel: 'gasTypeEth',
    gasName: 'Gwei',
    gasSettingName: 'Price',
  },
};

export function getChainIcon(symbol: string): string {
  return CHAIN_INFO[symbol]?.icon || CHAIN_INFO.SOL.icon;
}
export const CHAIN_ID_MAP: Record<string, string> = {
  [SOLANA_CHAIN_ID]: 'SOL',
  [BSC_CHAIN_ID]: 'BSC',
  [ETHEREUM_CHAIN_ID]: 'ETH',
  [BASE_CHAIN_ID]: 'BASE',
};
export const DEFAULT_CHAIN_ID = SOLANA_CHAIN_ID;
export const DEFAULT_CHAIN_SHORT_NAME = CHAIN_ID_MAP[DEFAULT_CHAIN_ID];
export function checkChainId(chainId?: string): string {
  if (chainId === undefined) {
    return DEFAULT_CHAIN_ID;
  }
  if (chainId === '') {
    return DEFAULT_CHAIN_ID;
  }
  if (!CHAIN_ID_MAP[chainId]) {
    return DEFAULT_CHAIN_ID;
  }
  return chainId;
}

export function getChainNativeToken(chainId?: string): string {
  return CHAIN_INFO[CHAIN_ID_MAP[checkChainId(chainId)]].symbol;
}
export function getShortNameByChainId(chainId?: string): string {
  return CHAIN_INFO[CHAIN_ID_MAP[checkChainId(chainId)]].shortName;
}
export function getChainInfoByChainId(chainId?: string): ChainInfo {
  return CHAIN_INFO[CHAIN_ID_MAP[checkChainId(chainId)]];
}
export function getDefaultChainInfo(): ChainInfo {
  return CHAIN_INFO[checkChainId()];
}
export function getAllGasTypeIdList(): Array<
  Pick<ChainInfo, 'gasTypeName' | 'gasTypeNameCamel'>
> {
  return Object.values(CHAIN_INFO).map((item) => ({
    gasTypeName: item.gasTypeName,
    gasTypeNameCamel: item.gasTypeNameCamel,
  }));
}
